import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Tag,
  Table,
  Spin,
  Alert,
  Row,
  Col,
  Typography,
  Space,
  Breadcrumb,
} from 'antd';
import ClassificationTag from '../components/ClassificationTag';
import SideBySide from '../components/SideBySide';
import { getCaseDetail } from '../services/api';

const { Title, Text } = Typography;

const STATUS_CONFIG = {
  TP: { color: 'green', label: 'True Positive' },
  FP: { color: 'red', label: 'False Positive' },
  FN: { color: 'orange', label: 'False Negative' },
};

const CLASSIFICATION_BG = {
  'Major': '#fff1f0',
  'Minor': '#fff7e6',
  'Incidental': '#e6f4ff',
  'No Changes': '#f6ffed',
};

const CaseDetail = () => {
  const { studyFk } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await getCaseDetail(studyFk);
        setData(result);
      } catch (err) {
        setError(err.message || 'Failed to load case details');
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [studyFk]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading case details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Alert
          type="error"
          message="Error"
          description={error}
          showIcon
          style={{ borderRadius: 12 }}
          action={<a onClick={() => navigate('/cases')} style={{ color: '#1A3C7A' }}>Back to Cases</a>}
        />
      </div>
    );
  }

  if (!data) return null;

  // Flatten the nested API response
  const patient = data.patient_info || {};
  const audit = data.audit || {};
  const aiObs = data.ai_observations || {};
  const classification = audit.classification || '';
  const classComment = audit.classification_comment || '';
  const auditType = audit.type || 'None';
  const normalAbnormal = audit.normal_abnormal || '';
  const studyType = audit.study_type || '';
  const aiObsText = aiObs.observations || '';
  const radContent = data.radiologist_content || '';
  const studyName = aiObs.study_name || patient.study_desc || '';
  const pathologies = data.pathologies || [];

  const pathologyColumns = [
    {
      title: 'Pathology',
      dataIndex: 'pathology',
      key: 'pathology',
      render: (text, record) => (
        <Space>
          <Text
            strong
            style={{
              textTransform: 'capitalize',
              color: record.is_in_scope === false ? '#aaa' : undefined,
            }}
          >
            {text}
          </Text>
          {record.is_in_scope === false && (
            <Tag style={{ background: '#f5f5f5', color: '#aaa', border: '1px dashed #ddd', borderRadius: 6, fontSize: 11 }}>
              out of scope
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 120,
      render: (text, record) => (
        <Text style={{ color: record.is_in_scope === false ? '#aaa' : undefined }}>{text}</Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 180,
      render: (status, record) => {
        if (record.is_in_scope === false) {
          return (
            <Tag style={{ background: '#f5f5f5', color: '#aaa', border: '1px dashed #ddd', borderRadius: 8, fontWeight: 600 }}>
              Out of Scope
            </Tag>
          );
        }
        const config = STATUS_CONFIG[status] || { color: 'default', label: status };
        return <Tag color={config.color} style={{ fontWeight: 600, borderRadius: 8 }}>{status} - {config.label}</Tag>;
      },
    },
  ];

  return (
    <div>
      {/* Breadcrumb */}
      <Breadcrumb
        style={{ marginBottom: 12 }}
        items={[
          {
            title: <a onClick={() => navigate('/cases')} style={{ color: '#1A3C7A', fontWeight: 500 }}>Cases</a>,
          },
          {
            title: <Text style={{ color: '#555' }}>{studyName || `Study ${data.study_fk}`}</Text>,
          },
        ]}
      />

      {/* Patient Info */}
      <Card size="small" style={{ marginBottom: 16, borderRadius: 12 }}>
        <Descriptions
          title={
            <Space>
              <Title level={5} style={{ margin: 0 }}>{studyName}</Title>
              <ClassificationTag classification={classification} style={{ fontSize: 14, padding: '2px 12px', borderRadius: 8 }} />
            </Space>
          }
          bordered
          size="small"
          column={{ xs: 1, sm: 2, lg: 3 }}
        >
          <Descriptions.Item label="Age">{patient.patient_age || '--'}</Descriptions.Item>
          <Descriptions.Item label="Sex">{patient.patient_sex || '--'}</Descriptions.Item>
          <Descriptions.Item label="Accession Number">{data.accession_number || '--'}</Descriptions.Item>
          <Descriptions.Item label="Study FK">{data.study_fk || '--'}</Descriptions.Item>
          <Descriptions.Item label="Study Type">{studyType || '--'}</Descriptions.Item>
          <Descriptions.Item label="Normal/Abnormal">
            <Tag color={normalAbnormal === 'NORMAL' ? 'green' : 'orange'} style={{ borderRadius: 8 }}>
              {normalAbnormal || '--'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Clinical Info" span={2}>
            {patient.study_desc || '--'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Audit Result */}
      <Card
        size="small"
        style={{
          marginBottom: 16,
          background: CLASSIFICATION_BG[classification] || '#fafafa',
          border: '1px solid #e8e8e8',
          borderRadius: 12,
        }}
      >
        <Row gutter={[16, 8]}>
          <Col xs={24}>
            <Space wrap>
              <Text type="secondary">Classification:</Text>
              <ClassificationTag classification={classification} style={{ fontSize: 15, padding: '4px 16px', borderRadius: 8 }} />
            </Space>
          </Col>
          {auditType && auditType !== 'None' && (
            <Col xs={24}>
              <Text type="secondary">Type: </Text>
              <Text strong>{auditType}</Text>
            </Col>
          )}
          <Col xs={24}>
            <Text type="secondary">Comment: </Text>
            <Text>{classComment || 'No comment'}</Text>
          </Col>
        </Row>
      </Card>

      {/* Side by Side Reports */}
      <div style={{ marginBottom: 16 }}>
        <SideBySide
          leftTitle={`Bionic AI Observations — ${aiObs.study_name || ''}`}
          rightTitle={`Radiologist Report — ${patient.study_desc || ''}`}
          leftContent={aiObsText}
          rightContent={radContent}
        />
      </div>

      {/* Pathologies */}
      {pathologies.length > 0 && (
        <Card title="Pathology Analysis" size="small" style={{ borderRadius: 12 }}>
          <Table
            dataSource={pathologies}
            columns={pathologyColumns}
            rowKey={(r, i) => `${r.pathology}-${i}`}
            size="small"
            pagination={false}
          />
        </Card>
      )}
    </div>
  );
};

export default CaseDetail;
